// Work Steps landing page. Work steps are listed grouped by OpsAudit
// (collapsible sections, ungrouped last) — decided in wayfinder ticket 04.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDebounce } from "../../../hooks/useDebounce";
import { Button } from "../../../components/ui/button";
import { LoadingSpinner } from "../../../components/ui/loadingSpinner/loadingSpinner";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "../../../components/ui/resizable";
import {
  ConfiguredState,
  isConfiguredState,
} from "../../../models/audit/control";
import { WorkStepWithUser } from "../../../models/audit/workStep";
import {
  useCreateWorkStepSession,
  useOpsAudits,
  useWorkStepFilterOptions,
  useWorkStepSessions,
  useWorkSteps,
} from "../../../hooks/useWorkSteps";
import { useAuth } from "../../../contexts/authContext.tsx";
import { useWorkStepOptroImport } from "../../../contexts/workStepOptroImportContext";
import { useImportingWorkSteps } from "../../../hooks/useImportingWorkSteps";
import { useOptroImportInterest } from "../../../hooks/useOptroImportInterest";
import { hasPermission } from "../../../lib/permissions";
import { PermissionGate } from "../../../components/PermissionGate";
import { WorkStepsFilterBar } from "./components/WorkStepsFilterBar";
import { WorkStepsGroupedTable } from "./components/WorkStepsGroupedTable";
import { WorkStepDetailPanel } from "./components/WorkStepDetailPanel";
import { OptroAccessPanelGate } from "../../../components/audit/optroAccess/OptroAccessPanelGate";
import { OptroAccessListNotice } from "../../../components/audit/optroAccess/OptroAccessListNotice";
import { useOptroViewAccess } from "../../../hooks/useOptroViewAccess";

export const WorkStepsPage = () => {
  const { workspaceMember } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filters, initialized from URL query params (mirrors ControlsPageV2)
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search") || "",
  );
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [configuredStateFilters, setConfiguredStateFilters] = useState<
    ConfiguredState[]
  >(searchParams.getAll("configuredState").filter(isConfiguredState));
  const [sectionFilters, setSectionFilters] = useState<string[]>(
    searchParams.getAll("section").filter(Boolean),
  );
  const [preparerFilters, setPreparerFilters] = useState<string[]>(
    searchParams.getAll("preparer").filter(Boolean),
  );
  const [opsAuditFilters, setOpsAuditFilters] = useState<string[]>(
    searchParams.getAll("opsAudit").filter(Boolean),
  );
  const [sortBy, setSortBy] = useState<string>(
    searchParams.get("sortBy") || "",
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">(
    searchParams.get("sortOrder") === "asc" ? "asc" : "desc",
  );
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [selectedWorkStep, setSelectedWorkStep] =
    useState<WorkStepWithUser | null>(null);

  const workStepIdParam = searchParams.get("workStepId");

  const { filterOptions } = useWorkStepFilterOptions();
  const {
    opsAudits,
    loading: loadingOpsAudits,
    refetch: refetchOpsAudits,
  } = useOpsAudits();
  const { createSession, loading: loadingCreateSession } =
    useCreateWorkStepSession();

  // The Audit filter reads the audits useOpsAudits already fetched, so the
  // full audits list is consumed once per page and /filter-options no longer
  // needs to embed its own copy.
  const filterBarOptions = useMemo(
    () => ({ ...filterOptions, opsAudits }),
    [filterOptions, opsAudits],
  );

  const filters = useMemo(
    () => ({
      searchTerm: debouncedSearchTerm || undefined,
      configuredStates:
        configuredStateFilters.length > 0 ? configuredStateFilters : undefined,
      sections: sectionFilters.length > 0 ? sectionFilters : undefined,
      preparers: preparerFilters.length > 0 ? preparerFilters : undefined,
      opsAuditIds: opsAuditFilters.length > 0 ? opsAuditFilters : undefined,
      sortBy: sortBy || undefined,
      sortOrder,
    }),
    [
      debouncedSearchTerm,
      configuredStateFilters,
      sectionFilters,
      preparerFilters,
      opsAuditFilters,
      sortBy,
      sortOrder,
    ],
  );

  const {
    workSteps,
    totalWorkStepsCount,
    optroAccessUnavailable,
    loading,
    loadMore,
    hasMore,
    refetch,
    refresh,
  } = useWorkSteps(filters);

  // Real Optro imports land via the normal work steps list fetch; each time
  // an import finishes, refresh so newly-created work steps show up (in
  // place, keeping pages already scrolled in). The audits list must refetch
  // too: imports find-or-create OpsAudits, and groupWorkSteps drops rows
  // whose opsAuditId the stale list can't resolve.
  const { completedImportTick, registerInterest } = useWorkStepOptroImport();
  useOptroImportInterest(registerInterest);
  const refreshedTickRef = useRef(completedImportTick);
  useEffect(() => {
    if (refreshedTickRef.current === completedImportTick) return;
    refreshedTickRef.current = completedImportTick;
    refresh();
    refetchOpsAudits();
  }, [completedImportTick, refresh, refetchOpsAudits]);

  const selectedWorkStepId = selectedWorkStep?.id ?? null;
  // Optro view access for the detail panel; its sessions wait for it too.
  const panelAccess = useOptroViewAccess("work-step", selectedWorkStepId);
  const {
    sessions,
    loading: loadingSessions,
    refetch: refetchSessions,
  } = useWorkStepSessions(
    panelAccess.state === "allowed" ? selectedWorkStepId : null,
  );

  useEffect(() => {
    if (!loading || workSteps.length > 0) {
      setIsInitialLoad(false);
    }
  }, [loading, workSteps.length]);

  // Sync filters to URL
  useEffect(() => {
    const params = new URLSearchParams();

    if (workStepIdParam) params.set("workStepId", workStepIdParam);
    if (searchTerm) params.set("search", searchTerm);
    configuredStateFilters.forEach((state) =>
      params.append("configuredState", state),
    );
    sectionFilters.forEach((section) => params.append("section", section));
    preparerFilters.forEach((preparer) => params.append("preparer", preparer));
    opsAuditFilters.forEach((opsAuditId) =>
      params.append("opsAudit", opsAuditId),
    );
    if (sortBy) params.set("sortBy", sortBy);
    if (sortOrder) params.set("sortOrder", sortOrder);

    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params, { replace: true });
    }
  }, [
    searchTerm,
    configuredStateFilters,
    sectionFilters,
    preparerFilters,
    opsAuditFilters,
    sortBy,
    sortOrder,
    workStepIdParam,
    searchParams,
    setSearchParams,
  ]);

  // Sync URL param -> selected work step
  useEffect(() => {
    if (!workStepIdParam) {
      if (selectedWorkStep) {
        setSelectedWorkStep(null);
      }
      return;
    }
    if (selectedWorkStep?.id === workStepIdParam) return;
    const match = workSteps.find((workStep) => workStep.id === workStepIdParam);
    if (match) {
      setSelectedWorkStep(match);
    }
  }, [workStepIdParam, workSteps, selectedWorkStep]);

  const clearFilters = () => {
    setSearchTerm("");
    setConfiguredStateFilters([]);
    setSectionFilters([]);
    setPreparerFilters([]);
    setOpsAuditFilters([]);
  };

  // `debouncedSearchTerm` is included alongside `searchTerm`: mirrors
  // ControlsPageV2 — for the debounce window after clearing the search the
  // (possibly empty) result set is still filtered, so the welcome state
  // below doesn't flash for a moment on a cleared no-match search.
  const hasActiveFilters =
    searchTerm ||
    debouncedSearchTerm ||
    configuredStateFilters.length > 0 ||
    sectionFilters.length > 0 ||
    preparerFilters.length > 0 ||
    opsAuditFilters.length > 0;

  // Each work step that lands mid-import reloads the list (and the audits,
  // which the import may have just created), so the table fills in as the
  // import runs instead of all at once when it finishes.
  const handleImportItemLanded = useCallback(() => {
    refresh();
    refetchOpsAudits();
  }, [refresh, refetchOpsAudits]);
  const { visibleWorkSteps, importingWorkSteps } = useImportingWorkSteps({
    workSteps,
    opsAudits,
    loading: loading || loadingOpsAudits,
    enabled: !hasActiveFilters,
    onItemLanded: handleImportItemLanded,
  });

  // Mirrors ControlsPageV2's showingWelcomeState: when the workspace has no
  // work steps at all, the table shows the welcome entry cards and the
  // filter/search bar above it is hidden (nothing to filter). A first import
  // in flight shows the table instead, with a placeholder row per work step.
  const showingWelcomeState =
    !loading &&
    totalWorkStepsCount === 0 &&
    importingWorkSteps.length === 0 &&
    !hasActiveFilters;

  const activeFilterCount = [
    configuredStateFilters.length > 0,
    sectionFilters.length > 0,
    preparerFilters.length > 0,
    opsAuditFilters.length > 0,
  ].filter(Boolean).length;

  // Infinite scroll observer setup
  const observer = useRef<IntersectionObserver | null>(null);
  const lastWorkStepRef = useCallback(
    (node: HTMLTableRowElement | null) => {
      if (loading) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMore();
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore, loadMore],
  );

  const handleWorkStepClick = (workStep: WorkStepWithUser) => {
    setSelectedWorkStep(workStep);
    const next = new URLSearchParams(searchParams);
    next.set("workStepId", workStep.id);
    setSearchParams(next);
  };

  const closeDetailPanel = () => {
    setSelectedWorkStep(null);
    const next = new URLSearchParams(searchParams);
    next.delete("workStepId");
    setSearchParams(next);
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  const handleCreateSession = useCallback(async () => {
    if (!selectedWorkStep) return;
    // One test per work step, named after it — no dialog. Refetch even on
    // failure: a 409 means the test already exists somewhere else.
    await createSession(selectedWorkStep.id, selectedWorkStep.name);
    refetchSessions();
  }, [selectedWorkStep, createSession, refetchSessions]);

  const selectedOpsAuditName = selectedWorkStep?.opsAuditId
    ? (opsAudits.find((opsAudit) => opsAudit.id === selectedWorkStep.opsAuditId)
        ?.name ?? null)
    : null;

  if (loading && isInitialLoad) {
    return (
      <div className="flex items-center justify-center min-h-[400px] w-full">
        <LoadingSpinner />
      </div>
    );
  }

  const leftPanel = (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Page header */}
      <div className="shrink-0 px-6 pt-6 flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Work Steps</h1>
          <p className="text-stone-600">
            Manage and view your operational audit work steps
          </p>
        </div>
        <div className="flex items-center gap-4 pb-0.5">
          {!showingWelcomeState && (
            <div className="text-sm text-stone-600">
              {workSteps.length} of {totalWorkStepsCount} work steps
            </div>
          )}
          <PermissionGate permission="edit_workstep">
            <Button onClick={() => navigate("/audit/work-steps/create")}>
              Create New Work Step
            </Button>
          </PermissionGate>
        </div>
      </div>

      {!showingWelcomeState && (
        <div className="shrink-0 px-6 pt-4 pb-3 mt-4 border-t border-transparent">
          <WorkStepsFilterBar
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            configuredStateFilters={configuredStateFilters}
            onConfiguredStateFiltersChange={setConfiguredStateFilters}
            sectionFilters={sectionFilters}
            onSectionFiltersChange={setSectionFilters}
            preparerFilters={preparerFilters}
            onPreparerFiltersChange={setPreparerFilters}
            opsAuditFilters={opsAuditFilters}
            onOpsAuditFiltersChange={setOpsAuditFilters}
            filterOptions={filterBarOptions}
            isFiltersOpen={isFiltersOpen}
            onFiltersOpenChange={setIsFiltersOpen}
            activeFilterCount={activeFilterCount}
            hasActiveFilters={Boolean(hasActiveFilters)}
            onClearFilters={clearFilters}
          />
        </div>
      )}
      <OptroAccessListNotice
        unavailable={optroAccessUnavailable}
        onRetry={refetch}
        className="mx-6 mb-3 w-auto shrink-0"
      />
      <div className="flex-1 overflow-y-auto overflow-x-auto px-6 pb-6">
        <WorkStepsGroupedTable
          workSteps={visibleWorkSteps}
          opsAudits={opsAudits}
          importingWorkSteps={importingWorkSteps}
          selectedWorkStep={selectedWorkStep}
          onWorkStepClick={handleWorkStepClick}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          lastWorkStepRef={lastWorkStepRef}
          hasActiveFilters={Boolean(hasActiveFilters)}
          loading={loading}
        />
      </div>
    </div>
  );

  return (
    <ResizablePanelGroup
      autoSaveId="work-steps-view"
      direction="horizontal"
      className="w-full max-w-[2500px] mx-auto"
    >
      <ResizablePanel id="left-panel" order={1} defaultSize={100} minSize={30}>
        <div className="h-screen">{leftPanel}</div>
      </ResizablePanel>

      {selectedWorkStep && (
        <>
          <ResizableHandle />
          <ResizablePanel
            id="right-panel"
            order={2}
            defaultSize={40}
            minSize={10}
          >
            <OptroAccessPanelGate
              kind="work-step"
              access={panelAccess}
              onClose={closeDetailPanel}
            >
              <WorkStepDetailPanel
                workStep={selectedWorkStep}
                opsAuditName={selectedOpsAuditName}
                sessions={sessions}
                loadingSessions={loadingSessions}
                onClose={closeDetailPanel}
                onCreateSession={handleCreateSession}
                loadingCreateSession={loadingCreateSession}
                canEditTest={hasPermission(
                  workspaceMember,
                  "edit_workstep_test",
                )}
                onSessionDeleted={refetchSessions}
              />
            </OptroAccessPanelGate>
          </ResizablePanel>
        </>
      )}
    </ResizablePanelGroup>
  );
};
